import React, { useState, useCallback } from 'react';
import { Toolbar, Typography, CircularProgress } from '@mui/material';
import { Add as AddIcon, Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AddCellModal } from './AddCellModal';
import axiosInstance from '../../../constants/axiosInstance';
import { CustomField, useOCRStore } from '../store/ocrStore';
import { createSavePayload } from '../helper/ocrUtils';
import { toast } from 'react-toastify';
import Button from '../../../core-components/theme/components/Button/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const saveConfiguration = async (payload: any) => {
  const { data } = await axiosInstance().post('/ocr/createOrUpdateLogbookAssetConfiguration', payload);
  return data;
};

export const RightPanelHeader: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { tables, customFields, selectedTemplate, annotations, addCustomField, resetAll } =
    useOCRStore(
      state => ({
        tables: state.tables,
        customFields: state.customFields,
        selectedTemplate: state.selectedTemplate,
        annotations: state.annotations,
        addCustomField: state.addCustomField,
        resetAll: state.resetAll,
      })
    );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { plantId, logbookAssetConfigurationId } = useParams();

  const saveMutation = useMutation({
    mutationFn: saveConfiguration,
    onSuccess: (data) => {
      toast.success(t('logbookConfig.configurationSavedSuccess', 'Configuration saved successfully!'));
      queryClient.invalidateQueries({ queryKey: ['configuredLogbooks'] });
      resetAll();
      navigate(-1);
    },
    onError: (error) => {
      toast.error(t('logbookConfig.saveFailedWithReason', 'Save failed: {{msg}}', { msg: error.message }));
    },
  });

  const handleSave = () => {
    const payload = createSavePayload(tables, customFields);
    const annotationIds = Object.values(annotations)
      .map(anno => anno.backendAnnotationId)
      .filter((id): id is string => !!id);
    if (payload.length === 0) {
      toast.error(t('logbookConfig.noDataToSave', 'There is no data to save. Please assign sensor tags to OCR data or add custom fields.'));
      return;
    }
    const finalPayload = {
      logbookConfiguration: payload,
      assetId: plantId ?? '',
      logbookTemplateId: selectedTemplate?.id,
      logbookAssetConfigurationId: logbookAssetConfigurationId ?? '',
      annotationIds,
      sectionInfo: 'this is section info'
    }

    saveMutation.mutate(finalPayload);
  };

  const handleSaveCustomField = useCallback(
    (field: Omit<CustomField, 'id'>) => {
      addCustomField(field);
    },
    [addCustomField]
  );

  return (
    <>
      <AddCellModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCustomField}
      />
      <Toolbar sx={{ gap: 2, position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          {t('logbookConfig.extractedAndCustomData', 'Extracted & Custom Data')}
        </Typography>
        {/* <Button
          variant="contained"
          size='small'
          startIcon={<AddIcon />}
          onClick={() => setIsModalOpen(true)}
          disabled={saveMutation.isPending}
        >
          Add Field
        </Button> */}
        <Button
          variant="contained"
          size='small'
          startIcon={
            saveMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? t('logbookConfig.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </Toolbar>
    </>
  );
};
